import React, { useEffect, useState } from "react";

import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Confirms one bulk action over the selected tickets.
 * Calls onConfirm(extra) with the fields the action needs
 * (userId / queueId for transfer, closeReasonId for close).
 */
const BulkActionDialog = ({
	action,
	count,
	queues,
	users,
	onClose,
	onConfirm,
	busy,
}) => {
	const open = !!action;
	const [userId, setUserId] = useState("");
	const [queueId, setQueueId] = useState("");
	const [reasonId, setReasonId] = useState("");
	const [reasons, setReasons] = useState([]);
	const [required, setRequired] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open) return undefined;
		setUserId("");
		setQueueId("");
		setReasonId("");
		if (action !== "close") return undefined;

		let cancelled = false;
		setLoading(true);
		api
			.get("/close-reasons")
			.then(({ data }) => {
				if (cancelled) return;
				setReasons(data.reasons || []);
				setRequired(!!data.required);
			})
			.catch(err => !cancelled && toastError(err))
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [open, action]);

	const invalid =
		(action === "transfer" && userId === "" && queueId === "") ||
		(action === "close" && (loading || (required && reasonId === "")));

	const submit = () =>
		onConfirm({
			userId: userId === "" ? undefined : userId,
			queueId: queueId === "" ? undefined : queueId,
			closeReasonId: reasonId === "" ? undefined : reasonId,
		});

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
			<DialogTitle>
				{action ? i18n.t(`supervisor.actions.${action}.title`, { total: count }) : ""}
			</DialogTitle>
			<DialogContent dividers>
				{action && (
					<Typography variant="body2" gutterBottom>
						{i18n.t(`supervisor.actions.${action}.description`, { total: count })}
					</Typography>
				)}

				{action === "transfer" && (
					<>
						<TextField
							select
							fullWidth
							variant="outlined"
							margin="dense"
							label={i18n.t("supervisor.transfer.user")}
							value={userId}
							onChange={e => setUserId(e.target.value)}
						>
							<MenuItem value="">{i18n.t("supervisor.transfer.noUser")}</MenuItem>
							{users.map(u => (
								<MenuItem key={u.id} value={u.id}>
									{u.name}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							fullWidth
							variant="outlined"
							margin="dense"
							label={i18n.t("supervisor.transfer.queue")}
							value={queueId}
							onChange={e => setQueueId(e.target.value)}
							helperText={i18n.t("supervisor.transfer.help")}
						>
							<MenuItem value="">{i18n.t("supervisor.transfer.keepQueue")}</MenuItem>
							{queues.map(q => (
								<MenuItem key={q.id} value={q.id}>
									{q.name}
								</MenuItem>
							))}
						</TextField>
					</>
				)}

				{action === "close" &&
					(loading ? (
						<CircularProgress size={24} />
					) : (
						(reasons.length > 0 || required) && (
							<TextField
								select
								fullWidth
								variant="outlined"
								margin="dense"
								label={
									required
										? `${i18n.t("quality.reason")} *`
										: i18n.t("quality.reason")
								}
								helperText={
									required
										? i18n.t("quality.reasonRequired")
										: i18n.t("quality.reasonOptional")
								}
								value={reasonId}
								onChange={e => setReasonId(e.target.value)}
							>
								{!required && (
									<MenuItem value="">{i18n.t("quality.reasonNone")}</MenuItem>
								)}
								{reasons.map(r => (
									<MenuItem key={r.id} value={r.id}>
										{r.name}
									</MenuItem>
								))}
							</TextField>
						)
					))}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={busy} variant="outlined" color="secondary">
					{i18n.t("quality.cancel")}
				</Button>
				<Button
					onClick={submit}
					disabled={busy || invalid}
					variant="contained"
					color="primary"
				>
					{busy ? (
						<CircularProgress size={20} />
					) : (
						i18n.t(`supervisor.actions.${action || "transfer"}.confirm`, { total: count })
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default BulkActionDialog;
